import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';
import {
  DoctorInfo,
  SpecializationInfo,
} from '../../models/Settings/ClinicSettings';

export interface SpecializationDoctorCatalogState {
  specializations: SpecializationInfo[];
  doctors: DoctorInfo[];
  /** Маппинг specId → массив doctorId */
  specDoctorMap: Record<string, string[]>;
  status: Status;
}

const initialState: SpecializationDoctorCatalogState = {
  specializations: [],
  doctors: [],
  specDoctorMap: {},
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

interface RawDoctor {
  id: string;
  surname: string;
  givenName: string;
  patronym: string;
  tenantId?: string;
  hisId?: string;
  hisSecondaryId?: string;
  deleted?: boolean;
  dateAdded?: string;
}

interface CatalogPayload {
  specializations: SpecializationInfo[];
  doctors: DoctorInfo[];
  specDoctorMap: Record<string, string[]>;
}

/**
 * Загружает специализации и врачей для клиники.
 * 1. GET /api/specialization?clinicIds=…  → список специализаций
 * 2. Для каждой специализации параллельно GET /api/doctor?specializationIds=specId
 *    (это единственный способ получить связь врач↔специализация из текущего API)
 * 3. Строит маппинг specId → doctorIds и обогащает DoctorInfo полем specializations
 */
export const loadSpecializationsAndDoctors = createAsyncThunk(
  'specializationDoctorCatalog/loadAll',
  async (clinicIds: string[], { rejectWithValue }) => {
    try {
      const paramsSerializer = (params: any) =>
        qs.stringify(params, { arrayFormat: 'repeat' });

      // 1. Загрузить специализации
      const specsResponse = await apiClientWithApi.get<SpecializationInfo[]>(
        'specialization',
        { params: { clinicIds }, paramsSerializer },
      );
      const specializations = specsResponse.data;

      // 2. Для каждой специализации параллельно загрузить врачей
      const specDoctorMap: Record<string, string[]> = {};
      const allDoctorsMap = new Map<string, DoctorInfo>();

      await Promise.all(
        specializations.map(async (spec) => {
          const res = await apiClientWithApi.get<RawDoctor[]>('doctor', {
            params: { specializationIds: [spec.id] },
            paramsSerializer,
          });

          const doctorIds: string[] = [];
          for (const raw of res.data) {
            doctorIds.push(raw.id);

            const existing = allDoctorsMap.get(raw.id);
            if (existing) {
              const specs = existing.specializations ?? [];
              if (!specs.some((s) => s.id === spec.id)) {
                specs.push({ id: spec.id, title: spec.title });
              }
              existing.specializations = specs;
            } else {
              allDoctorsMap.set(raw.id, {
                ...raw,
                specializations: [{ id: spec.id, title: spec.title }],
              });
            }
          }

          specDoctorMap[spec.id] = doctorIds;
        }),
      );

      const doctors = Array.from(allDoctorsMap.values());

      return { specializations, doctors, specDoctorMap } as CatalogPayload;
    } catch (error) {
      if (axios.isAxiosError(error)) return rejectWithValue(error.message);
      return rejectWithValue('Не удалось загрузить специализации и врачей!');
    }
  },
);

export const specializationDoctorCatalogSlice = createSlice({
  name: 'specializationDoctorCatalog',
  initialState,
  reducers: {
    clearCatalog(state) {
      state.specializations = [];
      state.doctors = [];
      state.specDoctorMap = {};
      state.status.responseLoading = false;
      state.status.responseError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadSpecializationsAndDoctors.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(loadSpecializationsAndDoctors.fulfilled, (state, action) => {
        state.specializations = action.payload.specializations;
        state.doctors = action.payload.doctors;
        state.specDoctorMap = action.payload.specDoctorMap;
        state.status.responseLoading = false;
      })
      .addCase(loadSpecializationsAndDoctors.rejected, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError =
          (action.payload as string) ??
          'Не удалось загрузить специализации и врачей';
      });
  },
});

export const { clearCatalog } = specializationDoctorCatalogSlice.actions;

export default specializationDoctorCatalogSlice.reducer;

export const selectCatalogSpecializations = (state: RootState) =>
  state.specializationDoctorCatalog.specializations;
export const selectCatalogDoctors = (state: RootState) =>
  state.specializationDoctorCatalog.doctors;
export const selectSpecDoctorMap = (state: RootState) =>
  state.specializationDoctorCatalog.specDoctorMap;
export const selectCatalogStatus = (state: RootState) =>
  state.specializationDoctorCatalog.status;
